import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Sidebar } from "../components/Sidebar";
import { Editor } from "../components/Editor";
import { TopBar } from "../components/TopBar";
import { EmptyState } from "../components/EmptyState";
import { MainNav } from "../components/MainNav";
import { ExternalFileBrowser } from "../components/ExternalFileBrowser";
import TodoPage from "./TodoPage";
import OrdersPage from "./OrdersPage";
import AdminPage from "./AdminPage";
import FileManagerPage from "./FileManagerPage";
import HistoryPage from "./HistoryPage";
import CommonNotesPage from "./CommonNotesPage";
import CommonTodosPage from "./CommonTodosPage";
import UserSettingsPage from "./UserSettingsPage";
import ApiDocsPage from "./ApiDocsPage";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const LAST_FILE_KEY = "notekeeper_last_file";
const encodePath = (path) => path.split('/').map(encodeURIComponent).join('/');

export default function Dashboard({ activeModule = "my-notes" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Memorează intenția de a căuta un text într-un anumit fișier (Acum trimitem tot obiectul cu timestamp)
  const [searchRequest, setSearchRequest] = useState(null);
  
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [sessionRestored, setSessionRestored] = useState(false);
  
  const [localContent, setLocalContent] = useState(""); 
  const [lastSyncedContent, setLastSyncedContent] = useState("");
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [remoteNewContent, setRemoteNewContent] = useState("");

  const [externalBrowserOpen, setExternalBrowserOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [groupFile, setGroupFile] = useState(null);
  const [fileContext, setFileContext] = useState("personal");

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true); // Asigură-te că pe desktop revine la loc
      }
    };
    window.addEventListener('resize', checkMobile);
    // Verificăm și la mount inițial
    checkMobile();
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleFileSelect = async (path) => {
    setSearchRequest(null); 
    await loadFile(path);
    if (isMobile) setSidebarOpen(false);
  };

  const fetchFiles = useCallback(async () => {
    if (activeModule !== "my-notes") return;
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/files`);
      setFiles(response.data);
      return response.data;
    } catch (error) {
      toast.error("Failed to load files");
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [activeModule]);

  const restoreLastFile = useCallback(async (fileList) => {
    if (sessionRestored) return;
    try {
      const lastFilePath = sessionStorage.getItem(LAST_FILE_KEY);
      if (lastFilePath && fileList.length > 0) {
        const findFile = (items, path) => {
          for (const item of items) {
            if (item.path === path) return true;
            if (item.children && findFile(item.children, path)) return true;
          }
          return false;
        };
        if (findFile(fileList, lastFilePath)) {
          await loadFile(lastFilePath);
        }
      }
    } catch (e) {
      console.error("Failed to restore session:", e);
    }
    setSessionRestored(true);
  }, [sessionRestored]);

  useEffect(() => {
    if (activeModule === "my-notes") {
      fetchFiles().then(fileList => {
        if (fileList.length > 0) restoreLastFile(fileList);
      });
    }
  }, [fetchFiles, activeModule, restoreLastFile]);

  useEffect(() => {
    if (selectedFile?.path && fileContext === "personal") {
      sessionStorage.setItem(LAST_FILE_KEY, selectedFile.path);
    }
  }, [selectedFile, fileContext]);

  useEffect(() => {
    let interval;
    if (selectedFile && fileContext === "personal") {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API_URL}/files/${encodePath(selectedFile.path)}`);
          const serverContent = response.data.content.replace(/^\uFEFF/, '');

          if (serverContent !== lastSyncedContent) {
            if (localContent !== lastSyncedContent) {
              setRemoteNewContent(serverContent);
              setConflictModalOpen(true);
            } else {
              setFileContent(serverContent);
              setLocalContent(serverContent);
              setLastSyncedContent(serverContent);
              toast.info("Fișier actualizat cu ultimele modificări.");
            }
          }
        } catch (error) {}
      }, 5000); 
    }
    return () => clearInterval(interval);
  }, [selectedFile, fileContext, lastSyncedContent, localContent]);

  const loadFile = async (path) => {
    try {
      const response = await axios.get(`${API_URL}/files/${encodePath(path)}`);
      const cleanContent = response.data.content.replace(/^\uFEFF/, '');
      
      setSelectedFile({ path, name: path.split("/").pop() });
      setFileContent(cleanContent);
      setLocalContent(cleanContent);
      setLastSyncedContent(cleanContent);
      setConflictModalOpen(false);
      setFileContext("personal");
      setGroupFile(null);
    } catch (error) {
      toast.error("Failed to load file");
    }
  };

  const saveFile = async (content) => {
    if (!selectedFile && !groupFile) return;
    setIsSaving(true);
    try {
      if (fileContext === "personal" && selectedFile) {
        await axios.put(`${API_URL}/files/${encodePath(selectedFile.path)}`, { content });
      } else if (fileContext === "external" && selectedFile?.connection) {
        await axios.post(`${API_URL}/external/write/${selectedFile.connection.id}`, {
          path: selectedFile.path,
          content
        });
      }
      setLastSyncedContent(content);
      setLocalContent(content);
    } catch (error) {
      toast.error("Failed to save file");
      throw error; 
    } finally {
      setIsSaving(false);
    }
  };

  const createFile = async (name, parentPath = "", isFolder = false) => {
    let actualIsFolder = isFolder;
    let actualParentPath = parentPath;

    if (typeof parentPath === "boolean") {
      actualIsFolder = parentPath;
      actualParentPath = "";
    }

    if (!actualParentPath && selectedFile?.path) {
      const pathParts = selectedFile.path.split("/");
      pathParts.pop();
      actualParentPath = pathParts.join("/"); 
    }

    try {
      const response = await axios.post(`${API_URL}/files`, {
        name: actualIsFolder ? name : (name.endsWith(".md") ? name : `${name}.md`),
        parent_path: actualParentPath,
        is_folder: actualIsFolder,
        content: ""
      });
      fetchFiles();
      if (!actualIsFolder) loadFile(response.data.path);
      toast.success(actualIsFolder ? "Folder created" : "Note created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create");
    }
  };

  const deleteFile = async (path) => {
    // Adaugă fereastra de confirmare nativă
    const isConfirmed = window.confirm("Ești sigur că vrei să ștergi acest element?");
    
    // Dacă utilizatorul apasă pe 'Cancel', oprim execuția funcției
    if (!isConfirmed) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/files/${encodePath(path)}`);
      if (selectedFile?.path === path) {
        setSelectedFile(null);
        setFileContent("");
        setLocalContent("");
        setLastSyncedContent("");
      }
      fetchFiles();
      toast.success("Moved to recycle bin");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const renameFile = async (oldPath, newName) => {
    try {
      const response = await axios.post(`${API_URL}/files/rename`, { old_path: oldPath, new_name: newName });
      
      // Obținem noua cale din răspunsul serverului, sau o calculăm local dacă serverul nu o returnează
      const pathParts = oldPath.split("/");
      pathParts.pop(); // Scoatem numele vechi
      const newPath = response.data?.new_path || (pathParts.length > 0 ? `${pathParts.join("/")}/${newName}` : newName);

      // Dacă fișierul pe care tocmai l-ai redenumit este fix fișierul deschis acum în editor...
      if (selectedFile && selectedFile.path === oldPath) {
        // Actualizăm selectedFile ca să știe editorul noul nume și noua cale la următoarea salvare
        setSelectedFile({
          ...selectedFile,
          path: newPath,
          name: newName
        });
      }

      fetchFiles();
      toast.success("Renamed");
    } catch (error) {
      toast.error("Failed to rename");
    }
  };

  const moveFile = async (sourcePath, destPath) => {
    try {
      await axios.post(`${API_URL}/files/move`, { source_path: sourcePath, dest_path: destPath });
      fetchFiles();
      toast.success("Moved");
    } catch (error) {
      toast.error("Failed to move");
    }
  };

  const togglePin = async (path) => {
    try {
      await axios.post(`${API_URL}/files/pin`, { file_path: path });
      fetchFiles();
    } catch (error) {
      toast.error("Failed to toggle pin");
    }
  };

  const handleExternalFileSelect = (fileData) => {
    setSelectedFile({
      path: fileData.path,
      name: fileData.name,
      connection: fileData.connection
    });
    setFileContent(fileData.content);
    setFileContext("external");
    setGroupFile(null);
  };

  const getCurrentFileName = () => {
    if (fileContext === "group" && groupFile) return groupFile.name;
    if (selectedFile) return selectedFile.name;
    return "";
  };

  const handleSearchResultSelect = (result) => {
    const filePath = result.path || result.file_path;
    const searchPayload = { 
      path: filePath, 
      snippet: result.snippet || "", 
      query: result.query || "",
      matchIndex: result.matchIndex !== undefined ? result.matchIndex : 0,
      timestamp: Date.now() 
    };

    if (result.type === "personal-note") {
      navigate("/"); 
      loadFile(filePath); 
      setSearchRequest(searchPayload); 
    } else if (result.type === "common-note") {
      navigate("/common-notes", { state: { searchRequest: searchPayload } });
    } else if (result.type === "order") {
      navigate("/orders");
    } else if (result.type === "personal-todo") {
      navigate("/my-todos");
    } else if (result.type === "common-todo") {
      navigate("/common-todos");
    }
  };

  const renderModuleContent = () => {
    switch (activeModule) {
      case "my-todos": 
        return <TodoPage sidebarOpen={sidebarOpen} closeSidebarMobile={() => isMobile && setSidebarOpen(false)} />;
      case "common-todos": 
        return <CommonTodosPage sidebarOpen={sidebarOpen} closeSidebarMobile={() => isMobile && setSidebarOpen(false)} />;
      case "orders": return <OrdersPage />;
      case "admin": return <AdminPage />;
      case "files": return <FileManagerPage />;
      case "recycle-bin": return <HistoryPage />;
      case "settings": return <UserSettingsPage />;
      case "common-notes": return <CommonNotesPage sidebarOpen={sidebarOpen} />;
      case "api-docs": return <ApiDocsPage />;
      case "my-notes":
      default:
        return (
          <div className="flex-1 flex overflow-hidden relative">
            <Sidebar
              files={files}
              selectedFile={fileContext === "personal" ? selectedFile : null}
              isOpen={sidebarOpen}
              isLoading={isLoading}
              onSelectFile={handleFileSelect}
              onCreateFile={createFile}
              onDeleteFile={deleteFile}
              onRenameFile={renameFile}
              onMoveFile={moveFile}
              onTogglePin={togglePin}
            />
            
            <main className="flex-1 overflow-hidden relative">
              {(selectedFile || groupFile) ? (
                <div className="h-full max-w-4xl mx-auto px-4 md:px-0">
                  <Editor
                    key={selectedFile?.path || groupFile?.path}
                    content={fileContent}
                    fileName={getCurrentFileName()}
                    onSave={saveFile}
                    isSaving={isSaving}
                    fileContext={fileContext}
                    onContentChange={(newContent) => setLocalContent(newContent)}
                    searchRequest={searchRequest?.path === (selectedFile?.path || groupFile?.path) ? searchRequest : null}
                  />
                </div>
              ) : (
                <EmptyState onCreateFile={() => createFile("Untitled")} />
              )}
            </main>
          </div>
        );
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      <TopBar 
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        sidebarOpen={sidebarOpen}
        onSelectSearchResult={handleSearchResultSelect}
      />
      
      <div className="flex-1 flex overflow-hidden relative">
        <MainNav activeModule={activeModule} isAdmin={user?.is_admin} />
        {renderModuleContent()}

        <ExternalFileBrowser
          isOpen={externalBrowserOpen}
          onClose={() => {
            setExternalBrowserOpen(false);
            setSelectedConnection(null);
          }}
          connection={selectedConnection}
          onFileSelect={handleExternalFileSelect}
        />
        
        {conflictModalOpen && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-background border border-border p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <h3 className="text-xl font-semibold mb-2 text-foreground">Conflict de modificare detectat!</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Se pare că cineva a modificat acest fișier în cloud în timp ce tu lucrai la el. Cum dorești să procedezi?
              </p>
              <div className="flex flex-col gap-3">
                <button
                  className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-left"
                  onClick={() => {
                    setFileContent(remoteNewContent);
                    setLocalContent(remoteNewContent);
                    setLastSyncedContent(remoteNewContent);
                    setConflictModalOpen(false);
                    toast.success("Fișier actualizat cu versiunea din cloud.");
                  }}
                >
                  <span className="font-medium">Descarcă versiunea din cloud</span>
                  <span className="block text-xs opacity-80 mt-1">vei pierde modificările tale nesalvate</span>
                </button>
                <button
                  className="w-full px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors text-left"
                  onClick={() => {
                    setLastSyncedContent(localContent); 
                    setConflictModalOpen(false);
                    toast.success("Ai păstrat versiunea ta. Nu uita să o salvezi pentru a suprascrie cloud-ul.");
                  }}
                >
                  <span className="font-medium">Păstrează versiunea mea</span>
                  <span className="block text-xs opacity-80 mt-1">vei suprascrie cloud-ul la următoarea salvare</span>
                </button>
                <button
                  className="w-full px-4 py-2 bg-secondary text-secondary-foreground border border-border rounded-md hover:bg-muted transition-colors mt-2"
                  onClick={() => setConflictModalOpen(false)}
                >
                  Anulează (decid mai târziu)
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}